'use client';

import { useCallback, useEffect } from 'react';
import config from 'config';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useGetSystemPreferencesQuery } from 'services/userProfileApi';

import { Link } from '@chakra-ui/next-js';
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setButtonVisible } from 'store/reducers/membershipsSlice';

import {
  canAccessShop,
  canPurchasePackages,
  filterShopItems,
  getShopDestination
} from 'utils';

import { ShopItemInterface } from 'types';

import { Container } from 'components';
import { Divider, MultiStepHeader } from 'ui';
import { ShopItem } from './components';
import { ROUTES } from '../../constant';

const {
  shop: {
    title,
    subtitle,
    description,
    isDefaultItems,
    defaultItems,
    shopItems,
    paddings,
    isImageClick,
    isShopBtnVisible,
    mobileItemScale,
    ...shopFlags
  }
} = config;

const Shop = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { data: sysPrefs } = useGetSystemPreferencesQuery('');
  const { packagesPromo } = useAppSelector((state) => state.packages);

  const {
    colors: {
      palette: { shop }
    }
  } = useTheme();

  // TODO: Adjust promo logic after BE provide necessary information
  const isPromoActive = packagesPromo?.active_promo;

  const getList = useCallback(() => {
    let list = filterShopItems([...shopItems], sysPrefs, shopFlags);

    if (isPromoActive && canPurchasePackages(sysPrefs, shopFlags)) {
      const activePromos = packagesPromo?.promo_labels || [];
      const activeItem = activePromos[0];

      const promoItem = {
        pkg_label_id: activeItem?.pkg_label_id || -1 * shopItems.length,
        img: packagesPromo?.tile_img || '',
        title: packagesPromo?.title || '',
        description: packagesPromo?.description,
        linkUrl: ROUTES.packagesShop,
        ...(activeItem?.pkg_label_id === -1 * shopItems.length && {
          linkUrl: ROUTES.packagesShop
        })
      };
      list.splice(1, 0, promoItem);
      list = filterShopItems(list, sysPrefs, shopFlags);
    }

    return list;
  }, [isPromoActive, packagesPromo, sysPrefs]);

  const totalItems = getList();
  const visibleDefaultItems = filterShopItems(
    defaultItems,
    sysPrefs,
    shopFlags
  ) as ShopItemInterface[];

  useEffect(() => {
    if (!sysPrefs) return;

    if (!canAccessShop(sysPrefs, shopFlags)) {
      router.replace(ROUTES.home);
      return;
    }

    const dest = getShopDestination(sysPrefs, shopFlags);
    if (dest && dest !== ROUTES.shop && totalItems.length === 0) {
      router.replace(dest);
    }
  }, [router, sysPrefs, totalItems.length]);

  if (sysPrefs && !canAccessShop(sysPrefs, shopFlags)) {
    return null;
  }

  const onBannerClick = (path: string) => () => {
    if (path === ROUTES.membershipShop) {
      dispatch(setButtonVisible(true));
    }
  };

  const getColumnsLength = () => {
    switch (totalItems.length) {
      case 1:
        return 1;
      case 3:
        return 3;
      default:
        return 2;
    }
  };

  return (
    <Container px={{ base: '15px', md: '25px' }} maxW='1330px'>
      <Box>
        {isPromoActive && (
          <Box mx={{ base: '-15px', md: 0 }}>
            <Image
              src={packagesPromo?.banner_img}
              alt='shop-banner'
              priority
              width={1120}
              height={378}
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        )}

        <MultiStepHeader
          title={title}
          subTitle={description}
          headingColor={shop.heading}
          descriptionColor={shop.description}
          mt={{ base: '40px', md: '50px', lg: '60px' }}
          mb={['0px', '0px', '25px']}
          descriptionPX={paddings}
        />
        {subtitle && (
          <Text
            textAlign='center'
            fontWeight='semibold'
            color={shop.subtitle}
            fontSize={[14, 16, 18]}
          >
            {subtitle}
          </Text>
        )}

        <Stack gap={0} mx='auto'>
          <Divider
            display={{ base: 'none', md: 'block' }}
            bg={shop.divider}
            border='none'
            h='1px'
            mt={{ base: '30px', md: isImageClick ? '35px' : '45px' }}
            mb={{ base: '30px', md: isImageClick ? '90px' : '45px' }}
          />
          {isDefaultItems ? (
            <Flex
              w='full'
              justifyContent='space-around'
              flexDirection={{ base: 'column', md: 'row' }}
              my={{ base: '20px', md: '0' }}
            >
              {visibleDefaultItems.map((item: ShopItemInterface) => (
                <Link
                  key={item.pkg_label_id}
                  borderRadius='10px'
                  _hover={{
                    shadow: 'lg'
                  }}
                  as={Button}
                  href={
                    item.linkUrl ||
                    `${ROUTES.packagesShop}/${item.pkg_label_id}`
                  }
                  onClick={onBannerClick(item?.linkUrl ?? '')}
                  variant='outline'
                  height='100%'
                  width='100%'
                  isExternal={item.isExternal}
                  cursor='pointer'
                  py={{ base: '20px', md: '40px' }}
                >
                  <VStack gap='20px'>
                    {item.icon && (
                      <item.icon
                        width='80px'
                        height='80px'
                        fill={shop.item.heading}
                      />
                    )}
                    <Heading
                      as='h3'
                      variant='h3'
                      fontWeight='regular'
                      color={shop.item.heading}
                      fontSize={[22, 24, 29]}
                      textAlign='center'
                      mt={{ base: 0, md: '30px' }}
                    >
                      {item.title}
                    </Heading>
                  </VStack>
                </Link>
              ))}
            </Flex>
          ) : (
            <SimpleGrid
              px={{
                base: 0,
                md: isImageClick ? '160px' : '40px'
              }}
              gap='60px'
              rowGap={{
                base: isImageClick ? '40px' : '50px',
                md: '90px'
              }}
              alignItems='stretch'
              flexWrap='wrap'
              mt={{ base: '45px', md: '0' }}
              justifyContent={{
                base: 'center',
                lg: isPromoActive ? 'space-between' : 'center'
              }}
              columns={{
                base: 1,
                md: getColumnsLength()
              }}
            >
              {totalItems.map((item: ShopItemInterface) => (
                <ShopItem
                  key={item.pkg_label_id}
                  item={item}
                  onClick={onBannerClick(item?.linkUrl ?? '')}
                  isImageClick={isImageClick}
                  isShopBtnVisible={isShopBtnVisible}
                  mobileItemScale={mobileItemScale}
                />
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default Shop;
